import { useEffect, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useCurrentSite } from '../../store/store.hooks'
import * as SurveyCore from 'survey-core'
import 'survey-core/survey-core.min.css'
import * as SurveyReact from 'survey-react-ui'

import {
  deleteSurveyAttachment,
  downloadSurveyAttachment,
  uploadSurveyAttachment,
} from '../../api/services/qualityControlService'
import pmpSurveyTheme from '../pmpSurveyTheme'

interface SurveyRendererProps {
  formJson: Record<string, unknown>
  mode?: 'display' | 'edit'
  initialData?: Record<string, unknown>
  onComplete?: (data: Record<string, unknown>) => void
}

interface FileQuestionWithRefreshApi {
  updateElementCss?: (force?: boolean) => void
}

interface SurveyFileItem {
  name?: string
  content?: unknown
}

const isRemoteHttpUrl = (value: string): boolean => {
  try {
    const parsed = new URL(value)
    return parsed.protocol === 'http:' || parsed.protocol === 'https:'
  } catch {
    return false
  }
}

const SurveyRenderer = ({ formJson, mode = 'display', initialData, onComplete }: SurveyRendererProps) => {
  const { i18n } = useTranslation()

  const currentSite = useCurrentSite()
  const currentSiteId = currentSite?.id ?? ''

  const downloadedObjectUrlsRef = useRef<Set<string>>(new Set())
  const surveyContainerRef = useRef<HTMLDivElement | null>(null)

  const trackObjectUrl = (objectUrl: string): string => {
    downloadedObjectUrlsRef.current.add(objectUrl)
    return objectUrl
  }

  const getProtectedObjectUrl = async (url: string): Promise<string> => {
    const objectUrl = await downloadSurveyAttachment(url, currentSiteId)
    return trackObjectUrl(objectUrl)
  }

  // Use ref so changes don't recreate the survey model
  const onCompleteRef = useRef(onComplete)
  onCompleteRef.current = onComplete

  const survey = useMemo(() => {
    const model = new SurveyCore.Model(formJson)
    model.applyTheme(pmpSurveyTheme)
    model.mode = mode
    model.locale = i18n.language === 'fr' ? 'fr' : 'en'
    model.showCompletedPage = false
    if (initialData) {
      model.data = initialData
    }
    model.onUploadFiles.add((_sender, options) => {
      const uploads = options.files.map((file) => uploadSurveyAttachment(file, currentSiteId).then((url) => ({ file, content: url })))
      Promise.all(uploads)
        .then((results) => options.callback('success', results))
        .catch(() => options.callback('error', []))
    })
    model.onClearFiles.add((_sender, options) => {
      // survey-core types declare fileName as `string`, but the library sends `null` at runtime
      // when clearing all files (form completion with showCompletedPage=false, or "Remove all").
      // In that case do nothing: submitted files must be preserved, and orphaned uploads from
      // cancelled sessions are acceptable (cleanup can be added later as a background job).
      if ((options.fileName as string | null) === null) {
        options.callback('success')
        return
      }
      const allFiles = (options.value ?? []) as Array<SurveyFileItem>
      const toDelete = allFiles.filter((f) => f.name === options.fileName)
      if (toDelete.length === 0) {
        options.callback('success')
        return
      }
      Promise.all(
        toDelete
          .filter((f): f is SurveyFileItem & { content: string } => typeof f.content === 'string')
          .map((f) => deleteSurveyAttachment(f.content, currentSiteId)),
      )
        .then(() => options.callback('success'))
        .catch(() => options.callback('error'))
    })
    model.onDownloadFile.add((_sender, options) => {
      const content = options.content
      if (typeof content !== 'string' || content.length === 0 || !isRemoteHttpUrl(content)) {
        options.callback('success', options.content)
        return
      }

      getProtectedObjectUrl(content)
        .then((objectUrl) => options.callback('success', objectUrl))
        .catch(() => options.callback('error'))
    })
    model.onComplete.add((sender) => {
      onCompleteRef.current?.(sender.data)
    })
    return model
  }, [formJson, mode, initialData, i18n.language])

  useEffect(() => {
    // Patch pour Task 16514 :
    // SurveyJS file actions can sporadically miss their first render. (SurveyJs v2.5.15)
    // As a lightweight patch, force a global refresh of all visible editable
    // file questions shortly after mount so the native "Select Files" action appears.
    const refreshFileQuestionsUi = () => {
      survey
        .getAllQuestions()
        .filter((question) => question.getType() === 'file' && question.isVisible && !question.isReadOnly)
        .forEach((question) => {
          const fileQuestion = question as unknown as FileQuestionWithRefreshApi
          fileQuestion.updateElementCss?.(true)
        })
    }

    const timeoutIds = [0, 150].map((delay) =>
      globalThis.setTimeout(() => {
        refreshFileQuestionsUi()
      }, delay),
    )

    const onProtectedFileLinkClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null
      const anchor = target?.closest('a[href]') as HTMLAnchorElement | null
      if (!anchor) {
        return
      }

      const href = anchor.getAttribute('href')
      if (!href || !isRemoteHttpUrl(href) || !anchor.hasAttribute('download')) {
        return
      }

      event.preventDefault()
      event.stopPropagation()

      getProtectedObjectUrl(href)
        .then((objectUrl) => window.open(objectUrl, '_blank', 'noopener,noreferrer'))
        .catch(() => undefined)
    }

    const surveyContainer = surveyContainerRef.current
    surveyContainer?.addEventListener('click', onProtectedFileLinkClick)

    return () => {
      surveyContainer?.removeEventListener('click', onProtectedFileLinkClick)

      timeoutIds.forEach((timeoutId) => {
        globalThis.clearTimeout(timeoutId)
      })

      downloadedObjectUrlsRef.current.forEach((objectUrl) => {
        URL.revokeObjectURL(objectUrl)
      })
      downloadedObjectUrlsRef.current.clear()
    }
  }, [survey])

  useEffect(() => {
    if (mode !== 'display') {
      return
    }

    let isDisposed = false

    const hydrateSavedAttachmentPreviews = async () => {
      const fileQuestions = survey.getAllQuestions().filter((question) => question.getType() === 'file')

      await Promise.all(
        fileQuestions.map(async (question) => {
          const currentValue = survey.getValue(question.name)
          if (!Array.isArray(currentValue) || currentValue.length === 0) {
            return
          }

          let changed = false
          const hydratedValue = await Promise.all(
            currentValue.map(async (item) => {
              if (typeof item === 'string') {
                if (!isRemoteHttpUrl(item)) {
                  return item
                }

                try {
                  const objectUrl = await getProtectedObjectUrl(item)
                  changed = true
                  return objectUrl
                } catch {
                  return item
                }
              }

              if (!item || typeof item !== 'object') {
                return item
              }

              const fileItem = item as SurveyFileItem
              if (typeof fileItem.content !== 'string' || !isRemoteHttpUrl(fileItem.content)) {
                return item
              }

              try {
                const objectUrl = await getProtectedObjectUrl(fileItem.content)
                changed = true
                return { ...item, content: objectUrl }
              } catch {
                return item
              }
            }),
          )

          if (!isDisposed && changed) {
            survey.setValue(question.name, hydratedValue)
          }
        }),
      )
    }

    void hydrateSavedAttachmentPreviews()

    return () => {
      isDisposed = true
    }
  }, [mode, survey])

  return (
    <div ref={surveyContainerRef}>
      <SurveyReact.Survey model={survey} />
    </div>
  )
}

export default SurveyRenderer
